import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';

import type { RootState } from '../store';
import { updateSessionStatus } from '../store/authSlice';
import { getQueryParameters } from './regionApi';

const preUrl = '/api/v1/';

interface IReqProps {
  server: string;
  key?: string;
  accountIds?: string[];
  accountId?: string;
  refresh?: boolean;
  category?: string;
  description?: string;
  contact?: string;
  overall_rate?: number;
  ux_rate?: number;
  functionality_rate?: number;
  fcName?: string;
}

interface IGlobalUser {
  region: string;
  error_code: number;
  email: string;
  region_token: string;
  glob_token: string;
  group_region: Record<string, string>[];
  session_data?: Record<string, any>;
}

interface ErrorData {
  error_desc: string;
  error_code: number;
}

const normalizeServer = (server?: string) => {
  if (!server) return '';
  return server.replace(/\/api\/v1\/?$/, '');
};

export const runGlobalLogin = async (api: any, baseQuery: any) => {
  const state = api.getState() as RootState;
  const server = normalizeServer(state.auth.configs?.server);
  const key = state.auth.debugKey;
  console.log('runGlobalLogin with server:', server, 'debug_key:', key);
  const result = await api
    .dispatch(
      globalApi.endpoints.userLogin.initiate(
        {
          server,
          key
        },
        {
          forceRefetch: true
        }
      )
    )
    .unwrap();

  if (result) {
    console.log('Global login successful, token:', result.glob_token);
  }

  return result;
};

/**
 * Safe URL builder (prevents undefined concatenation bugs)
 */
const buildUrl = (server?: string, path: string = ''): string => {
  return `${server ?? ''}${preUrl}${path}`;
};

const baseQuery = fetchBaseQuery({
  baseUrl: '',
  mode: 'cors',
  credentials: 'include',
  // timeout: 60000,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.global?.glob_token;

    if (token) {
      headers.set('Authorization', `Token ${token}`);
    }

    headers.set('Content-type', 'application/json');
    headers.set('Accept', 'application/json');

    return headers;
  }
});

const baseQueryCatchError: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const error_data = result.error.data as ErrorData;

    console.warn('get 401 error:', JSON.stringify(error_data));

    if (error_data?.error_desc === 'Need SSO login first') {
      api.dispatch(updateSessionStatus('REVOKED'));
    } else {
      api.dispatch(updateSessionStatus('EXPIRED'));
    }
  }

  return result;
};

export const globalApi = createApi({
  reducerPath: 'globalApi',
  baseQuery: baseQueryCatchError,
  tagTypes: ['Regions', 'Accounts', 'Entitlements'],
  endpoints: (build) => ({
    getRegions: build.query<any[], IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'region'),
        method: 'GET'
      }),
      providesTags: ['Regions']
    }),
    getDemoInstances: build.query<any[], IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'demo_instances'),
        method: 'GET'
      })
    }),
    getRetentions: build.query<any[], IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'retention/user_list'),
        method: 'GET'
      })
    }),

    getRetentionById: build.query<any, Record<string, string>>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'retention/get_retention_by_id'),
        method: 'POST',
        body: {
          email: args?.email,
          id: args?.id
        }
      })
    }),

    sendRetention: build.mutation<any, Record<string, any>>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'retention'),
        method: 'PUT',
        body: args?.data
      })
    }),

    getCommonData: build.query<Record<string, any>, IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'fc/commondata'),
        method: 'GET'
      })
    }),

    getSystemData: build.query<Record<string, any>, IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'system/commondata'),
        method: 'GET'
      })
    }),

    getProductVersions: build.query<Record<string, any>, IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'system/get_product_versions'),
        method: 'GET'
      })
    }),

    getUserOrgNodes: build.query<Record<string, any>, IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'fc/user_org_nodes'),
        method: 'GET',
        params: {
          refresh: args?.refresh
        }
      }),
      providesTags: ['Accounts']
    }),

    getUserPermission: build.query<Record<string, any>, Record<string, any>>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'fc/org_node_user_permission'),
        method: 'POST',
        body: {
          account_node_id: args?.account_node_id,
          account_id: args?.account_id,
          user_id: args?.user_id,
          access_from_account_id: args?.access_from_account_id,
          access_from_user_id: args?.access_from_user_id
        }
      })
    }),

    getEntitlements: build.query<Record<string, any>, Record<string, any>>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'fc/entitlement'),
        method: 'POST',
        body: getQueryParameters(args)
      }),
      providesTags: ['Entitlements']
    }),

    userLogin: build.query<IGlobalUser, IReqProps>({
      query: (args): FetchArgs => ({
        url: args?.key
          ? buildUrl(args.server, `user/login?debug_key=${args.key}`)
          : buildUrl(args.server, 'user/login'),
        method: 'GET'
      })
    }),

    userSessionCheck: build.query<Record<string, any>, IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'user/session_check'),
        method: 'GET'
      })
    }),

    sendFeedback: build.mutation<any, IReqProps>({
      query: (args): FetchArgs => ({
        url: buildUrl(args.server, 'feedback'),
        method: 'POST',
        body: {
          category: args?.category,
          description: args?.description,
          contact: args?.contact,
          overall_rate: args?.overall_rate,
          ux_rate: args?.ux_rate,
          functionality_rate: args?.functionality_rate
        }
      })
    })
  })
});

export const {
  useGetRegionsQuery,
  useGetRetentionsQuery,
  useGetRetentionByIdQuery,
  useGetCommonDataQuery,
  useGetSystemDataQuery,
  useGetProductVersionsQuery,
  useUserLoginQuery,
  useUserSessionCheckQuery,
  useGetUserOrgNodesQuery,
  useGetUserPermissionQuery,
  useLazyGetUserPermissionQuery,
  useSendFeedbackMutation,
  useSendRetentionMutation,
  useGetEntitlementsQuery,
  useGetDemoInstancesQuery
} = globalApi;
